import AktLista from './AktLista';

import { useEffect, useState } from 'react';
import * as AktClient from './AktClient';
import { MetriaAktMetadata } from './types/MetriaAktMetadata';

import { Alert, Link } from '@mui/material';
import ErrorBoundary from '../ErrorBoundary';
import { NySokContainer } from '../NySokContainer';
import { removeStorageKey } from '../common/lib/storage';
import { LoadState } from '../types/LoadState';
import { LMAktMetadata } from './types/LMAktMetadata';

export const isValidAktbeteckning = (aktbeteckning: string): boolean => {
  aktbeteckning = aktbeteckning.trim();
  const regExp = new RegExp(/^(?:[0|1][0-9]|2[0-5]|99).{2,}/gm);
  return regExp.test(aktbeteckning);
};

export const aktformatStorageKey = 'aktformat';

export const SokAkt: React.FC = () => {
  const maxPollTime = 3600000; // 1 hour
  const pollInterval = 5000;

  const [isPolling, setIsPolling] = useState(false);
  const [_buyLoadState, setBuyLoadState] = useState(LoadState.Idle);
  const [_searchResult, setSearchResult] = useState<LMAktMetadata>();
  const [savedAkter, setSavedAkter] = useState<MetriaAktMetadata[]>([]);
  const [pollingError, setPollingError] = useState('');

  useEffect(() => startPolling(), []);

  const startPolling = (beteckning?: string) => {
    if (isPolling) {
      return;
    }
    setIsPolling(true);

    const getAllAkter = async (pollCount: number) => {
      if (pollCount * pollInterval > maxPollTime) {
        setIsPolling(false);
        return setPollingError('Nedladdning av akt tog för lång tid.');
      }
      const result = await AktClient.listAkter();
      if (result.success) {
        const akter = result.data;
        setSavedAkter(akter);
        if (
          (!beteckning ||
            akter.find((akt) => akt.aktbeteckning === beteckning)) &&
          !akter.find((akt) => akt.status !== 'HAMTAD')
        ) {
          setPollingError('');
          setIsPolling(false);
          setSearchResult(undefined);
          setBuyLoadState(LoadState.Idle);
        } else {
          setTimeout(() => getAllAkter(++pollCount), pollInterval);
        }
      } else {
        setPollingError(
          result.statusCode === 401 || result.statusCode === 403
            ? 'Du har blivit utloggad. Ladda om sidan och logga in igen.'
            : 'Oväntat fel när sparade akter skulle hämtas.'
        );
        if (result.statusCode >= 500) {
          setTimeout(() => getAllAkter(++pollCount), pollInterval);
        } else {
          setIsPolling(false);
        }
      }
    };

    getAllAkter(0);
  };

  return (
    <ErrorBoundary onError={() => removeStorageKey(aktformatStorageKey)}>
      <NySokContainer heading="Akt">
        <Alert severity="error" sx={{ mt: 2 }}>
          <p>
            För närvarande kan Metria inte tillhandahålla akter via
            FastighetSök. Vi hänvisar er tills vidare till Lantmäteriet för
            manuella beställningar av de akter ni behöver via länken:{' '}
            <Link
              href="https://www.lantmateriet.se/sv/om-lantmateriet/kontakta-oss/ta-del-av-allman-handling/"
              rel="noopener noreferrer"
              target="_blank"
            >
              Beställningsformulär
            </Link>
          </p>
          <p>
            Vi följer utvecklingen av situationen hos Lantmäteriet. Vår
            förhoppning är att kunna återgå till att erbjuda akter via
            FastighetSök inom en snar framtid.
          </p>
          <p>
            Från och med 2024-05-24 har Lantmäteriet stoppat åtkomsten till
            digitala akter av säkerhetsskäl. Stoppet gäller för närvarande på
            obestämd tid.
            <em style={{ display: 'block', textAlign: 'right' }}>
              Uppdaterat 2024-11-29
            </em>
          </p>
        </Alert>
      </NySokContainer>
      {/* Bortkommenterat i väntan på att LM ska fixa Akt Direkt. */}
      {/* <AktSearchForm
        buyLoadState={buyLoadState}
        searchResult={searchResult}
        setSearchResult={setSearchResult}
        setBuyLoadState={setBuyLoadState}
        startPolling={startPolling}
      /> */}
      {pollingError && <Alert severity="error">{pollingError}</Alert>}
      <AktLista
        akter={savedAkter}
        deleteAkter={async (ids: number[]) => {
          const result = await AktClient.taBortAkter(ids);
          if (result.success) {
            startPolling();
          }
        }}
      />
    </ErrorBoundary>
  );
};
